import { useQuery } from "@tanstack/react-query";

import { AmbulanzResponse, ambulanzesApi } from "@/api";
import { QueryKey } from "@/query/Query";

/**
 * Splits the entries into the open pool (offen + übernommen) and the completed (erledigt) ones,
 * and counts the open ones. Kept as a module-level function so its reference is stable and React
 * Query memoizes the result.
 */
function selectAmbulanzes(ambulanzes: AmbulanzResponse[]) {
  const open = ambulanzes.filter((ambulanz) => !ambulanz.isCompleted);
  const completed = ambulanzes.filter((ambulanz) => ambulanz.isCompleted);

  return { open, completed, openCount: open.length };
}

/**
 * Shared React Query for the ambulanz entries. Used by the Ambulanzen page, the Zentrale widget and
 * the sidebar badge. Returns the open pool (offen + übernommen) by default; pass
 * `includeCompleted` to also fetch completed (erledigt) ones (the full list page toggles this).
 * The flag is part of the query key so open-only and full result sets are cached separately, while
 * mutations invalidating `[QueryKey.Ambulanzes]` refresh both. The `select` splits the entries and
 * counts the open ones so consumers don't re-derive.
 */
export function useAmbulanzesQuery(includeCompleted = false) {
  return useQuery({
    queryKey: [QueryKey.Ambulanzes, includeCompleted],
    queryFn: () => ambulanzesApi.getAllAmbulanzes({ includeCompleted }),
    select: selectAmbulanzes,
  });
}
